import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgForm } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { VoucherService } from 'src/app/modules/finance/services/voucher.service';
import { VoucherTypeDto } from 'src/app/modules/finance/models/dto/voucherTypeDto';
import { VoucherTypeService } from 'src/app/modules/finance/services/voucher-type.service';
import { SupplierService } from 'src/app/modules/finance/services/supplier.service';
import { PortService } from 'src/app/shared/entities/port/port.service';

import {
  DynamicDialogConfig,
  DynamicDialogRef,
  DialogService,
} from 'primeng/dynamicdialog';
// IMPORTANTE: Importar o novo AppToastService
import { AppToastService } from 'src/app/shared/services/app-toast.service';

@Component({
  selector: 'app-voucher-modal',
  templateUrl: './voucher-modal.component.html',
  styleUrls: ['./voucher-modal.component.scss'],
  standalone: false,
  providers: [DialogService]
  // imports: [CheckboxModule]
})
export class VoucherModalComponent implements OnInit {
  @ViewChild('newEditForm') newEditForm: NgForm | undefined;
  model: any = {};
  voucherTypes: VoucherTypeDto[] = [];
  suppliers: any[] = [];
  ports: any[] = [];
  /** Skeleton do modal enquanto os gets de abertura (voucherTypes + voucher) não terminam. */
  initialLoading = true;
  /** Spinner do botão Save durante o submit — separado do skeleton. */
  submitting = false;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private voucherService: VoucherService,
    private voucherTypesService: VoucherTypeService,
    private supplierService: SupplierService,
    private portService: PortService,
    private toastService: AppToastService, // Substituído aqui
  ) {}

  ngOnInit(): void {
    const isEdit = !!this.config.data?.id;
    if (isEdit) this.model.id = this.config.data.id;

    // Une os gets de abertura: o skeleton só some quando os voucher types, suppliers,
    // ports e o voucher (no modo edição) terminarem — evita dropdowns vazios.
    forkJoin({
      voucherTypes: this.voucherTypesService.getAll(),
      suppliers: this.supplierService.getSuppliers(),
      ports: this.portService.getAllPorts(),
      voucher: isEdit ? this.voucherService.getVoucher(this.model.id) : of(null),
    })
      .pipe(finalize(() => (this.initialLoading = false)))
      .subscribe({
        next: ({ voucherTypes, suppliers, ports, voucher }) => {
          this.voucherTypes = voucherTypes as any;
          this.suppliers = (suppliers as any[]).filter((s) => s.isActive);
          this.ports = ports as any[];
          if (voucher) {
            this.model = voucher;
            this.model.portDefaultSuppliers = this.model.portDefaultSuppliers ?? [];
            if ((voucher as any).voucherType && !(voucher as any).voucherTypeId) {
              this.model.voucherTypeId = (voucher as any).voucherType.id;
            }
            setTimeout(() => {
              if (this.newEditForm) {
                this.newEditForm.form.markAsPristine();
                this.newEditForm.form.markAsUntouched();
              }
            }, 100);
          } else {
            this.model.portDefaultSuppliers = [];
          }
        },
        error: () => {},
      });
  }

  addPortDefault(): void {
    this.model.portDefaultSuppliers.push({ portId: null, suplierId: null });
  }

  removePortDefault(index: number): void {
    this.model.portDefaultSuppliers.splice(index, 1);
  }

  /** Portas repetidas na lista de defaults por porto — o backend também rejeita, mas
   *  avisar antes do submit evita um save que falha sem explicação na tela. */
  hasDuplicatePortDefaults(): boolean {
    const portIds = (this.model.portDefaultSuppliers ?? [])
      .map((p: any) => p.portId)
      .filter((portId: any) => portId !== null && portId !== undefined);

    return new Set(portIds).size !== portIds.length;
  }

  newOrSaveRecord() {
    if (this.hasDuplicatePortDefaults()) {
      this.toastService.error(
        'Invalid ports',
        'Each port can only have one default supplier. Remove the duplicate before saving.',
      );
      return;
    }

    this.submitting = true;
    if (!this.model.id) {
      this.model.updatedIn = new Date();
    }

    const isEdit = !!this.model.id;

    const request = isEdit
      ? this.voucherService.update(this.model.id, this.model)
      : this.voucherService.add(this.model);

    request.pipe(finalize(() => (this.submitting = false))).subscribe({
      next: (data: any) => {
        // Sucesso bonitão e personalizado!
        const title = isEdit ? 'Updated' : 'Created';
        const msg = isEdit
          ? `The voucher was successfully updated.`
          : `The voucher was added to the system.`;

        this.toastService.success(title, msg);

        if (this.newEditForm) this.newEditForm.form.markAsPristine();
        this.ref.close(isEdit ? 'success' : (data as any)?.id ?? 'success');
      },
      error: (error: unknown) => {
        const title = isEdit ? 'Update failed' : 'Creation failed';
        this.toastService.error(title, this.getErrorMessage(error));
      },
    });
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const payload = error.error;
      if (typeof payload === 'string' && payload.trim()) return payload;
      if (payload?.Message) return String(payload.Message);
      if (payload?.message) return String(payload.message);
      if (payload?.title) return String(payload.title);
    }

    return 'An unexpected error occurred.';
  }
}
